/**
 * TextField Component
 */

import * as classNames from 'classnames';
import { clone, isEqual, isFunction, isNil, trim } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { WrappedFieldInputProps, WrappedFieldMetaProps } from 'redux-form';
import './styles.scss';

import { getColorSchemeClassName } from '../../../styles/color-schemes';
import { ButtonProps } from '../../buttons/Button';
import { Span } from '../../text/Label';

interface IOwnProps {
  autoComplete?: boolean;
  autoFocus?: boolean;
  allowDigitsOnly?: boolean;
  appendPercentage?: boolean;
  className?: string;
  colorScheme?: 'light' | 'white' | 'transparent';
  disabled?: boolean;
  icon?: JSX.Element;
  inlineButton?: React.ReactElement<ButtonProps>;
  input?: WrappedFieldInputProps;
  inputProps?: any;
  isAutocomplete?: boolean;
  isControlled?: boolean;
  isParsed?: boolean;
  name: string;
  meta?: WrappedFieldMetaProps<any>;
  onBlur?: (event: any, value: string | number) => void;
  onChange?: (event: any, value: string | number) => void;
  onFocus?: (event: any, value: string | number) => void;
  onlyValidateIfTouched?: boolean;
  optional?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  size?: TextFieldSize;
  style?: React.CSSProperties;
  submitOnEnter?: ((value: string) => any) | boolean;
  type?: 'text' | 'password' | 'email';
  value?: string | number;
  withoutBorder?: boolean;
}

export type TextFieldProps = IOwnProps;

interface IOwnState {
  isFocus: boolean;
  value: string | number;
}

const classes = new BEM('textfield');

export default class TextField extends React.Component<IOwnProps, IOwnState> {
  static defaultProps: Partial<IOwnProps> = {
    optional: false,
    size: 'medium',
    type: 'text',
  };

  private rawStr = '';
  private caretPosition = 0;

  private input: HTMLInputElement;

  constructor(props: IOwnProps) {
    super(props);

    this.state = {
      isFocus: false,
      value: this.props.value || '',
    };
  }

  componentWillReceiveProps(nextProps: IOwnProps) {
    if (!nextProps.isControlled || isNil(nextProps.value)) {
      return;
    }

    if (this.state.value !== nextProps.value) {
      this.setState({ value: nextProps.value });
    }
  }

  shouldComponentUpdate(nextProps: IOwnProps, nextState: IOwnState) {
    return !(isEqual(nextProps, this.props) && isEqual(nextState, this.state));
  }

  componentDidUpdate({ input, isParsed }: IOwnProps) {
    if (this.props.input && input && !isParsed) {
      const currentValue = this.props.input.value;
      if (currentValue !== input.value) {
        const str = this.rawStr.substr(0, this.caretPosition);
        const index = String(currentValue).indexOf(str) + this.caretPosition;

        if (index !== -1) {
          this.input.selectionStart = this.input.selectionEnd = index;
        }
      }
    }
  }

  focus() {
    setTimeout(() => {
      this.input.focus();
      this.onToggleFocus({ target: this.input, type: 'focus' });
    }, 100);
  }

  render() {
    /* tslint:disable:no-unused-variable */
    const {
      autoComplete,
      autoFocus,
      children,
      className,
      colorScheme,
      disabled,
      icon,
      inlineButton,
      input,
      inputProps,
      meta,
      name = this.props.input ? this.props.input.name : '',
      optional,
      onlyValidateIfTouched,
      placeholder,
      readOnly,
      size,
      style,
      type,
      withoutBorder,
    } = this.props;

    const {
      onKeyDown = null,
      ...restInputProps
    } = inputProps || {};

    const modifiers = {
      'is-focus': this.state.isFocus,
      'is-disabled': disabled,
      'is-readonly': readOnly,
      [size!]: size,
      'error': meta && meta.touched && meta.error,
      'warning': meta && meta.touched && meta.warning,
      'no-border': !!withoutBorder,
    };

    const extraClasses = classNames(
      colorScheme && getColorSchemeClassName(colorScheme, 'textfield'),
      icon && 'with--icon',
      className,
    );

    const inputControlProps = input ?
      {
        onFocus: (e) => { this.onToggleFocus(e); input.onFocus(e, e.target.value, null); },
        onBlur: (e) => { this.onToggleFocus(e); input.onBlur(e, e.target.value, null); },
        onChange: (e) => { this.onChange(e); input.onChange(e, e.target.value, null); },
        value: input.value,
      } : {
        onFocus: this.onToggleFocus,
        onBlur: this.onToggleFocus,
        onChange: this.onChange,
        value: this.state.value,
      };

    const inlineButtonProps: ButtonProps = {
      onClick: this.onSubmit,
      className: 'textfield__inline-button',
      size: size === 'large' ? 'medium-large' : size === 'small' ? 'small' : 'small-medium',
      disabled,
    };
    const iconProps = {
      onClick: () => this.input.focus(),
      className: 'textfield__icon',
    };

    const InlineButton = inlineButton && React.cloneElement(inlineButton, inlineButtonProps);
    const Icon = icon && React.cloneElement(icon, iconProps);

    const labelClasses = new BEM('label');

    return (
      <div {...classes('', modifiers, extraClasses) } style={style}>
        <div {...classes('background') } />
        {children}
        {meta && (
          (((onlyValidateIfTouched && meta.touched) || !onlyValidateIfTouched) &&
            (meta.error &&
              <Span {...labelClasses('', 'error') } size="medium" textColor="dangerous">{meta.error}</Span>) ||
            (meta.warning &&
              <Span {...labelClasses('', 'warning') } size="medium">{meta.warning}</Span>)
          )
        )}
        <input
          type={type}
          ref={(node) => this.input = node!}
          name={name}
          onKeyPress={this.onKeyPress}
          onKeyDown={this.onKeyDown}
          placeholder={placeholder}
          autoComplete={autoComplete ? 'on' : 'off'}
          autoFocus={autoFocus}
          disabled={disabled}
          required={!optional}
          readOnly={readOnly}
          {...inputControlProps}
          {...restInputProps}
          {...classes('input') }
        />
        {Icon}
        {InlineButton}
      </div>
    );
  }

  onKeyPress: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter' && this.props.isAutocomplete) {
      e.preventDefault();
    }
  }

  onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    const {
      inlineButton,
      inputProps,
      submitOnEnter,
    } = this.props;

    const {
      onKeyDown = null,
    } = inputProps || {};

    if (onKeyDown != null) {
      onKeyDown!(clone(e));
    }

    if (e.key === 'Escape') {
      this.input.blur();
    } else if (submitOnEnter && e.key === 'Enter') {
      if (isFunction(submitOnEnter)) {
        submitOnEnter(this.input.value);
      } else if (inlineButton) {
        this.onSubmit();
      }
    }
  }

  onSubmit = () => {
    const {
      inlineButton,
    } = this.props;

    if (inlineButton && inlineButton.props.onClick) {
      inlineButton.props.onClick(this.input.value);
    }

    this.setState({
      value: '',
    });
    this.input.blur();
  }

  onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const event = clone(e);
    const newValue = e.target.value;

    this.rawStr = String(newValue);
    this.caretPosition = e.target.selectionEnd || 0;

    if (this.props.allowDigitsOnly && isNaN(Number(newValue))) {
      return;
    }

    this.setState({ value: newValue }, () => {
      if (this.props.onChange) {
        this.props.onChange(event, newValue);
      }
    });
  }

  onToggleFocus = (e) => {
    const event = clone(e);
    const eventType = e.type;
    const { value } = this.state;

    if (eventType === 'focus') {
      e.target.select();
    } else if (eventType === 'blur') {
      if (value[0] === '0' && this.props.allowDigitsOnly) {
        this.setState({ value: parseInt(String(value), 10) });
      }
      if (this.props.appendPercentage) {
        this.setState({ value: `${trim(String(value), '%')}%` });
      }
    }

    this.setState({
      isFocus: eventType === 'focus' ? true : false,
    }, () => {
      /* tslint:disable:no-unused-expression */
      (eventType === 'focus' && this.props.onFocus) && this.props.onFocus(event, value);
      (eventType === 'blur' && this.props.onBlur) && this.props.onBlur(event, value);
    });
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/atoms/forms/TextField/index.tsx